import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil, Building2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", logo: "", address: "", phone: "", email: "", website: "", industry: "", plan_id: "", status: "active", renewal_date: "", notes: "" };

export default function Companies() {
  const { t } = useLang();
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [{ data: c }, { data: p }] = await Promise.all([api.get("/companies"), api.get("/plans")]);
    setCompanies(c); setPlans(p);
  };

  useEffect(() => { load(); }, []);

  const filtered = companies.filter((c) => (c.name || "").toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      // Strip empty-string optional fields (backend EmailStr/HttpUrl reject '')
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        }
      });
      if (editing) {
        await api.patch(`/companies/${editing.company_id}`, payload);
        toast.success("Company updated");
      } else {
        await api.post("/companies", payload);
        toast.success("Company created");
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c });
    setOpen(true);
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const statusColor = { active: "bg-emerald-500", trial: "bg-amber-500", suspended: "bg-rose-500", cancelled: "bg-zinc-500" };

  return (
    <DashboardLayout
      title={t("companies.title")}
      subtitle={t("companies.subtitle")}
      right={
        <button data-testid="companies-new" onClick={openNew} className="btn-scale h-9 px-3 rounded-md bg-white text-zinc-950 font-medium text-sm flex items-center gap-2 hover:bg-zinc-100">
          <Plus className="w-4 h-4" /> {t("companies.new")}
        </button>
      }
    >
      <div className="flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="companies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full pl-9 pr-3 h-9 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100" />
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid="companies-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              <th className="p-3 font-medium">{t("companies.fields.name")}</th>
              <th className="p-3 font-medium">{t("companies.fields.industry")}</th>
              <th className="p-3 font-medium">{t("companies.fields.plan")}</th>
              <th className="p-3 font-medium">{t("companies.fields.status")}</th>
              <th className="p-3 font-medium">{t("companies.fields.created")}</th>
              <th className="p-3 font-medium text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.company_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-zinc-800 grid place-items-center overflow-hidden">
                      {c.logo ? <img src={c.logo} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-4 h-4 text-zinc-500" />}
                    </div>
                    <div>
                      <div className="text-zinc-100">{c.name}</div>
                      <div className="text-xs text-zinc-500">{c.email || "—"}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-zinc-400">{c.industry || "—"}</td>
                <td className="p-3 text-zinc-400">{plans.find(p => p.plan_id === c.plan_id)?.name || "—"}</td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-2 text-xs text-zinc-300">
                    <span className={`w-2 h-2 rounded-full ${statusColor[c.status] || "bg-zinc-500"} ${c.status === "active" ? "glow-green" : ""}`} />
                    {t(`companies.statuses.${c.status}`)}
                  </span>
                </td>
                <td className="p-3 text-zinc-500 text-xs">{c.created_at?.slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`edit-company-${c.company_id}`} onClick={() => openEdit(c)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-company-${c.company_id}`} onClick={() => remove(c.company_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500 text-sm">{t("common.no_data")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("common.edit") : t("companies.new")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-3">
            {[
              ["name", "companies.fields.name"], ["logo", "companies.fields.logo"],
              ["email", "companies.fields.email"], ["phone", "companies.fields.phone"],
              ["website", "companies.fields.website"], ["industry", "companies.fields.industry"],
              ["address", "companies.fields.address"],
              ["renewal_date", "companies.fields.renewal"],
            ].map(([k, tk]) => (
              <label key={k} className="block">
                <span className="overline block mb-1.5">{t(tk)}</span>
                <input
                  data-testid={`company-field-${k}`}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm"
                />
              </label>
            ))}
            <label className="block">
              <span className="overline block mb-1.5">{t("companies.fields.plan")}</span>
              <select data-testid="company-field-plan" value={form.plan_id || ""} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                <option value="">—</option>
                {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="overline block mb-1.5">{t("companies.fields.status")}</span>
              <select data-testid="company-field-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                {["active", "trial", "suspended", "cancelled"].map(s => <option key={s} value={s}>{t(`companies.statuses.${s}`)}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="overline block mb-1.5">{t("companies.fields.notes")}</span>
              <textarea data-testid="company-field-notes" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid="company-save" disabled={loading} onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-white text-zinc-950 text-sm font-medium disabled:opacity-70">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
